// @flow

import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { GSGrid } from "@components";
import type { SpecificDocumentType } from "@modules/shared/reducer/employee-events/types";
import actionCreators from "@modules/shared/actionCreator";
import * as selectors from "@modules/shared/selectors/employee-events";
import type { State } from "@store/reducers";
import { toTitleCase } from "@transformers/response";
import { formatDateTime } from "@transformers/date";
import { formatAuthoredBy } from "@transformers/names";
import type { EmployeeType } from "@modules/employee/types";
import { getEmployee } from "@modules/employee/selectors";
import { route } from "@routes/helper";
import { getCurrentPath } from "@selectors";

type OwnProps = $Exact<{
  data: Array<SpecificDocumentType>,
  employeeId: number,
  clientId: number,
}>;

type PropsFromState = $Exact<{
  employee: EmployeeType,
  showRemoveDocumentModal: boolean,
  documentId: number | null,
  documentName: string | null,
  currentPath?: string,
}>;

type PropsFromDispatch = $Exact<{
  onRemoveDocumentHandler: typeof actionCreators.ToggleRemoveEmployeeDocumentModal,
  onDismissRemoveDocumentHandler: Function,
  removeDocument: typeof actionCreators.RemoveEmployeeDocument,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class SpecificDocumentsList extends React.Component<Props> {
  get columns() {
    return [
      {
        key: "id",
        name: this.props.t("DOCUMENT_ID"),
      },
      {
        key: "absenceId",
        name: this.props.t("RELATED_ABSENCE_ID"),
        renderCell: ({ row }: { row: SpecificDocumentType }) => {
          return (
            <Link
              to={route(
                this.props.currentPath && this.props.currentPath.includes("reporting")
                  ? "REPORTING_EMPLOYEE_ABSENCE_DETAILS_PATH"
                  : "REPORTING_EMPLOYEE_ABSENCE_DETAILS_PATH",
                {
                  clientId: this.props.clientId,
                  employeeId: this.props.employeeId,
                  absenceId: row.absenceId || 0,
                },
              )}
            >
              {row.absenceId || this.props.t("N/A")}
            </Link>
          );
        },
      },
      {
        key: "documentType",
        name: this.props.t("DOCUMENT_TYPE"),
        renderCell: ({ row }: { row: SpecificDocumentType }) =>
          toTitleCase(this.props.t(row.documentType)),
        align: "left",
      },
      {
        key: "name",
        name: this.props.t("DOCUMENT_NAME"),
        align: "left",
      },
      {
        key: "createdBy",
        name: this.props.t("UPLOADED_BY"),
        renderCell: ({ row }: { row: SpecificDocumentType }) => formatAuthoredBy(row.createdBy),
        align: "left",
      },
      {
        key: "createdAt",
        name: this.props.t("DATE_UPLOADED"),
        renderCell: ({ row }: { row: SpecificDocumentType }) => formatDateTime(row.createdAt),
        align: "left",
      },
    ];
  }

  render() {
    return (
      <React.Fragment>
        <GSGrid rows={this.props.data} columns={this.columns} />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State, ownProps: OwnProps): PropsFromState => ({
  employee: getEmployee(ownProps.employeeId)(state),
  showRemoveDocumentModal: selectors.showRemoveDocumentModal(state),
  documentId: selectors.getDocumentId(state),
  documentName: selectors.getDocumentName(state),
  currentPath: getCurrentPath(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  onRemoveDocumentHandler: actionCreators.ToggleRemoveEmployeeDocumentModal,
  onDismissRemoveDocumentHandler: () =>
    actionCreators.ToggleRemoveEmployeeDocumentModal({ visible: false }),
  removeDocument: actionCreators.RemoveEmployeeDocument,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  localize(),
)(SpecificDocumentsList);
